import { useCallback, useEffect } from 'react';
import { useAddNote, useUpdateNote, useDeleteNote } from './queries/useNotes';
import { useUpdateTopic } from './queries/useTopics';
import { useToastUIStore } from '../store/toastUIStore';
import { logger } from '../utils/logger';
import { useRequestDeduplication } from './useRequestDeduplication';
/**
 * Custom hook to handle all note operations with consistent error handling
 * and topic timestamp updates
 */
export function useNotesOperations(topicId: string) {
  const addNoteMutation = useAddNote();
  const updateNoteMutation = useUpdateNote();
  const deleteNoteMutation = useDeleteNote();
  const updateTopicMutation = useUpdateTopic();

  const { addToast } = useToastUIStore();
  const { dedupedRequest, cancelAllRequests } = useRequestDeduplication();

  // Clean up all pending requests on unmount
  useEffect(() => {
    return () => {
      cancelAllRequests();
    };
  }, [cancelAllRequests]);

  // Handle note update WITHOUT updating topic timestamp on every save
  // This prevents excessive refetching during typing
  const updateNote = useCallback(
    async ({
      noteId,
      updates,
    }: {
      noteId: string;
      updates: { content?: string; summary?: string | null };
    }) => {
      const requestKey = `updateNote-${noteId}`;

      try {
        await dedupedRequest(
          requestKey,
          async signal => {
            // Check if request was aborted
            if (signal.aborted) {
              throw new Error('Request was cancelled');
            }

            return updateNoteMutation.mutateAsync({
              topicId,
              noteId,
              updates,
            });
          },
          { replaceExisting: true } // Cancel previous update for same note
        );

        // Don't update topic timestamp here - it causes excessive refetching
        // Topic timestamp should only be updated on create/delete or explicit user actions

        return true;
      } catch (error: any) {
        // Don't show error for cancelled requests
        if (error.message !== 'Request was cancelled') {
          logger.error('Failed to edit note:', error);
          addToast({
            type: 'error',
            message: 'Failed to save note',
            duration: 5000,
          });
        }
        return false;
      }
    },
    [topicId, updateNoteMutation, dedupedRequest, addToast]
  );

  // Handle note deletion with topic update
  const deleteNote = useCallback(
    async ({ noteId }: { noteId: string }) => {
      const requestKey = `deleteNote-${noteId}`;

      try {
        await dedupedRequest(
          requestKey,
          async signal => {
            if (signal.aborted) {
              throw new Error('Request was cancelled');
            }

            // Delete the note
            await deleteNoteMutation.mutateAsync({
              topicId,
              noteId,
            });

            // Update topic's last_used_at timestamp
            await updateTopicMutation.mutateAsync({
              topicId,
              updates: {
                last_used_at: new Date().toISOString(),
              },
            });
          },
          { replaceExisting: false } // Don't cancel existing deletes
        );

        return true;
      } catch (error: any) {
        if (error.message !== 'Request was cancelled') {
          logger.error('Failed to delete note:', error);
        }
        // Silent failure for empty note cleanup
        return false;
      }
    },
    [topicId, deleteNoteMutation, updateTopicMutation, dedupedRequest]
  );

  // Handle note creation with topic update
  const createNote = useCallback(
    async ({ content, summary = null }: { content: string; summary?: string | null }) => {
      // Use content hash for dedup key to prevent duplicate notes with same content
      const contentHash = btoa(content).substring(0, 10);
      const requestKey = `createNote-${topicId}-${contentHash}`;

      try {
        const newNote = await dedupedRequest(
          requestKey,
          async signal => {
            if (signal.aborted) {
              throw new Error('Request was cancelled');
            }

            const note = await addNoteMutation.mutateAsync({
              topicId,
              content,
              summary,
            });

            // Update the topic's last_used_at timestamp
            await updateTopicMutation.mutateAsync({
              topicId,
              updates: {
                last_used_at: new Date().toISOString(),
              },
            });

            return note;
          },
          { replaceExisting: false } // Don't cancel existing creates
        );

        return newNote;
      } catch (error: any) {
        if (error.message !== 'Request was cancelled') {
          logger.error('Failed to add note:', error);
          addToast({
            type: 'error',
            message: 'Failed to create note',
            duration: 5000,
          });
        }
        return null;
      }
    },
    [topicId, addNoteMutation, updateTopicMutation, dedupedRequest, addToast]
  );

  // Explicitly update topic timestamp when needed
  // This should be called sparingly (e.g., when opening a topic, not on every edit)
  const updateTopicTimestamp = useCallback(async () => {
    try {
      await updateTopicMutation.mutateAsync({
        topicId,
        updates: {
          last_used_at: new Date().toISOString(),
        },
      });
    } catch (error) {
      logger.error('Failed to update topic timestamp:', error);
    }
  }, [topicId, updateTopicMutation]);

  return {
    createNote,
    updateNote,
    deleteNote,
    updateTopicTimestamp,
    // Expose mutations for components that need loading states
    isAddingNote: addNoteMutation.isPending,
    isUpdatingNote: updateNoteMutation.isPending,
    isDeletingNote: deleteNoteMutation.isPending,
  };
}
